<template>
  <v-app
    id="main"
    :data-locale="$i18n.locale"
  >
    <component
      :is="layoutComponent"
      v-if="layoutComponent"
    />
    <template v-if="logged">
      <KpSnackbarQueue />
    </template>

    <div id="global-modal" />
    <ConfirmDialog
      v-if="isRunningChangeoversOpen"
      :title="$t('changeover.closure.closeChangeover')"
      remove-body-margin
      :show-close="true"
      :display-footer="false"
      @cancel="isRunningChangeoversOpen = false"
    >
      <template #default="props">
        <ChangeoverClosure
          :id="runningChangeover.changeover_event"
          :key="runningChangeover.changeover_event"
          ref="changeoverClosure"
          :footer-target-name="props.props.footerTargetName"
          @update:loading="closingChangeover = $event"
          @changeover-closed="closeChangeoverClosureModal"
        />
      </template>
    </ConfirmDialog>
    <ConfirmDialog
      v-if="commandLaunchId > 0 || commandLaunchId === 'start_onoff'"
      :display-footer="false"
      remove-body-margin
      :title="monitoringDialogTitle"
      :show-close="true"
      height="600px"
      :persistent="true"
      @cancel="closeMonitoringSwitcher"
    >
      <template #default="props">
        <MonitoringSwitcher
          :command-launch-id="commandLaunchId"
          :footer-target-name="props.props.footerTargetName"
          @cancel="closeMonitoringSwitcher"
        />
      </template>
    </ConfirmDialog>
    <ConfirmDialog
      v-if="$store.state.recurringEvents.openRecurringEventOccurrenceWizard"
      show-close
      :display-footer="false"
      :title="$t('event.details')"
      @cancel="closeOccurrenceWizard"
    >
      <template #default="props">
        <RecurringEventOccurrenceWizard
          :footer-target-name="props.props.footerTargetName"
          @cancel="closeOccurrenceWizard"
        />
      </template>
    </ConfirmDialog>
    <ChatBotWidget />
    <DisconnectModal v-if="showDisconnectModal" />
    <KpAnchorsInspector v-if="isAnchorsInspectionActive" />
  </v-app>
</template>

<script>
import {
  defineAsyncComponent, provide, watch, computed,
} from 'vue';
import userflow from 'userflow.js';

import {
  useSubscription,
} from '@vue/apollo-composable';
import gql from 'graphql-tag';

import { useLocale } from 'vuetify';
import { useI18n } from 'vue-i18n';
import { THEME_KEY } from 'vue-echarts';
import { mapGetters, mapMutations, useStore } from '~/store';
import useAppLayout from '~/composables/useAppLayout';
import useChangeover from '~/composables/useChangeover';
import useKpAnchorsInspection from '~/composables/useKpAnchorsInspection';
import useEventType from '~/composables/useEventType';
import useEChartDarkTheme from '~/composables/echarts/useEChartDarkTheme';
import useLanguage from '~/composables/useLanguage';
import useSharedDeviceConnectivity from '~/composables/useSharedDeviceConnectivity';
import useTracking from '~/composables/tracking/useTracking';
import useUserflow from '~/composables/useUserflow';
import useNavigation from '~/composables/navigation/useNavigation';
import useSnackbarQueue from '~/composables/useSnackbarQueue';

import KpSnackbarQueue from '~/components/common/keyprod/KpSnackbarQueue.vue';
import ConfirmDialog from '~/components/common/others/ConfirmDialog.vue';
import DefaultLayout from '~/layouts/DefaultLayout.vue';
import FocusLayout from '~/layouts/FocusLayout.vue';
import Loading from './Loading.vue';

const ChangeoverClosure = defineAsyncComponent(() => import('~/pages/navigation/bottom/events/components/ChangeoverClosure.vue'));
const ChatBotWidget = defineAsyncComponent(() => import('~/components/common/chatbot/ChatBotWidget.vue'));
const DisconnectModal = defineAsyncComponent(() => import('~/components/common/others/DisconnectModal.vue'));
const KpAnchorsInspector = defineAsyncComponent(() => import('~/components/common/kp-anchors/KpAnchorsInspector.vue'));
const MonitoringSwitcher = defineAsyncComponent(() => import('~/components/common/command-launches/MonitoringSwitcher.vue'));
const RecurringEventOccurrenceWizard = defineAsyncComponent(() => import('~/pages/settings/times/components/recurring-events/RecurringEventOccurrenceWizard.vue'));

export default {
  components: {
    ChangeoverClosure,
    ChatBotWidget,
    ConfirmDialog,
    DefaultLayout,
    FocusLayout,
    KpAnchorsInspector,
    KpSnackbarQueue,
    Loading,
    MonitoringSwitcher,
    RecurringEventOccurrenceWizard,
    DisconnectModal,
  },
  setup() {
    // Initialize the shared device connectivity
    useSharedDeviceConnectivity();

    // Store access for Composition API
    const store = useStore();

    // Sync the Vuetify and VueI18n locales
    const { current } = useLocale();
    const { locale } = useI18n();
    watch(
      locale,
      (value) => {
        current.value = value;
      },
      { immediate: true },
    );

    const { trackInit } = useTracking();
    trackInit({});
    const { setLanguage } = useLanguage();
    const { fetchFlatEventTypes } = useEventType();
    const {
      getUserflowUserId, getUserflowUserAttributes, getTenantAttributes, isUserflowEnabled,
    } = useUserflow();

    const { layoutComponent } = useAppLayout();
    const { isActive: isAnchorsInspectionActive } = useKpAnchorsInspection();

    // EChart theme
    const { theme: echartDarkTheme } = useEChartDarkTheme();
    provide(THEME_KEY, echartDarkTheme);

    const { selectedMachine, selectedChangeoverObject } = useNavigation();

    // Computed property for commandLaunchId using Composition API
    const commandLaunchId = computed(() => store.getters['commands/getCommandLaunchId']);

    // Use changeover composable to get selectedChangeover
    const { selectedChangeover } = useChangeover();
    const { addSnackbar, deleteSnackbar } = useSnackbarQueue();

    /**
     * Opens monitoring switcher component
     * and remove running changeover
     */
    const openMonitoringSwitcher = () => {
      // Detect if a command is active.
      if (selectedMachine.value?.last_event?.changeover_cl_trigger) {
        const deviceParams = selectedMachine.value?.machine_devices[0]?.device_params
          ? JSON.parse(selectedMachine.value?.machine_devices[0]?.device_params)
          : null;

        // If the counting is active show the learning process.
        // Only if it's not an auto learning machine.
        if (selectedMachine.value?.machine_count_type?.mct_name !== 'on_off' && !deviceParams?.auto_learning) {
          store.commit('commands/commitCommandLaunchId', selectedMachine.value?.last_event?.changeover_cl_trigger);
        }
      } else if (selectedChangeover.value?.changeover_cl_trigger) {
        store.commit('commands/commitCommandLaunchId', selectedChangeover.value?.changeover_cl_trigger);
      }
    };

    // Subscribe to learning requested
    const { result: resultLearningRequested } = useSubscription(
      gql`
        subscription learningRequested {
          learningRequested {
            machine
          }
        }
      `,
    );

    watch(resultLearningRequested, (data) => {
      if (parseInt(data?.learningRequested?.machine, 10) === selectedMachine.value?.machine_id) {
        openMonitoringSwitcher();
      }
    });

    return {
      fetchFlatEventTypes,
      getUserflowUserId,
      getUserflowUserAttributes,
      getTenantAttributes,
      isUserflowEnabled,
      isAnchorsInspectionActive,
      layoutComponent,
      setLanguage,
      openMonitoringSwitcher,
      commandLaunchId,
      addSnackbar,
      deleteSnackbar,
      machineSelected: selectedMachine,
      selectedChangeoverObject,
    };
  },
  data: () => ({
    logged: false,
    pidTimeOutRunningChangeovers: null,
    // Tells if we are "closing" a changeover
    closingChangeover: false,
  }),
  computed: {
    ...mapGetters({
      user: 'auth/user',
      showDisconnectModal: 'auth/showDisconnectModal',
      runningChangeover: 'changeovers/runningChangeover',
      isModalRunningChangeoverOpen: 'changeovers/isModalRunningChangeoverOpen',
    }),
    isMachineHaveChangeoverRunning() {
      const lastEvent = this.machineSelected?.last_event;
      if (lastEvent) {
        return lastEvent.event_end === null && lastEvent.event_type === 22;
      }
      return false;
    },
    machineSelectedEventId() {
      return this.machineSelected?.last_event?.event_id;
    },
    isRunningChangeoversOpen: {
      get() {
        // Don't open the popup if we're on a workshop dashboard
        const currentDashboard = this.getCurrentDashboard();
        if (currentDashboard?.categoryExpand?.dc_name === 'Atelier') {
          return false;
        }
        return this.runningChangeover !== null && this.isModalRunningChangeoverOpen;
      },
      set(val) {
        this.stateRunningChangeoverClosure(val);
      },
    },
    theme() {
      return (this.$vuetify.theme.dark) ? 'dark' : 'light';
    },
    /**
     * The title of the monitoring dialog
     */
    monitoringDialogTitle() {
      return this.$t('device.learnMonitoring.machineInformation', {
        // eslint-disable-next-line camelcase
        machine_name: this.machineSelected?.machine_name,
      });
    },
  },
  watch: {
    user: {
      async handler(newVal) {
        if (!newVal) {
          await this.setLanguage();
          this.logged = false;
          clearInterval(this.pidTimeOutRunningChangeovers);
          userflow.reset();
        } else {
          this.logged = true;
          await this.fetchFlatEventTypes();
          await this.setLanguage(newVal?.language);
          const userId = this.getUserflowUserId(newVal);
          const userAttributes = this.getUserflowUserAttributes(newVal);
          if (this.isUserflowEnabled()) {
            userflow.init(window.keyprod.USERFLOW_KEY);
            userflow.setResourceCenterLauncherHidden(true);
            await userflow.identify(userId, userAttributes);
          }
        }
      },
      deep: true,
      immediate: true,
    },
    isModalRunningChangeoverOpen(isOpen) {
      if (isOpen) {
        this.deleteSnackbar('Changeover_closure');
      }
    },
    isMachineHaveChangeoverRunning() {
      this.setRunningChangeover();
    },
    machineSelectedEventId() {
      this.setRunningChangeover();
    },
    machineSelected() {
      // Don't delete snackbar on machine dashboards if changeover is running
      const currentDashboard = this.getCurrentDashboard();
      if (currentDashboard?.categoryExpand?.dc_name === 'Machine' && this.runningChangeover) {
        return;
      }
      this.deleteSnackbar('Changeover_closure');
    },
    /**
     * Commit new command launch id when two navigator's windows opened
     */
    selectedChangeoverObject: {
      handler(val, oldVal) {
        if (oldVal !== undefined
          && val?.changeover_cl_trigger !== oldVal?.changeover_cl_trigger
          && !!this.commandLaunchId) {
          // We'll only change the command launch if a previous dialog was opened
          this.commitCommandLaunchId(val?.changeover_cl_trigger);
        }
      },
    },
    /**
     * Show snackbar when switching from workshop to machine dashboard
     */
    '$route.params.value': {
      handler(toId, fromId) {
        if (this.$route.name === 'dashboardId' && this.runningChangeover && fromId) {
          this.$nextTick(() => {
            const dashboards = this.$store.getters['dashboard/getDashboards'] ?? [];
            const prev = dashboards.find((d) => `${d.id}` === `${fromId}`);
            const curr = dashboards.find((d) => `${d.id}` === `${toId}`);
            // Show snackbar when switching from workshop to machine dashboard
            if (prev?.categoryExpand?.dc_name === 'Atelier' && curr?.categoryExpand?.dc_name === 'Machine') {
              this.addSnackbar({
                id: 'Changeover_closure',
                color: 'color_setups_and_adjustments',
                content: this.$t('changeover.changeoverRunningCanBeClosedFromMenu', { machineName: this.machineSelected?.machine_name }),
                timeout: -1,
                callback: 'openChangeoverClosure',
              });
            }
          });
        }
      },
    },
  },
  unmounted() {
    clearInterval(this.pidTimeOutRunningChangeovers);
  },
  methods: {
    ...mapMutations('changeovers', ['commitRunningChangeovers', 'commitIsModalRunningChangeoverOpen']),
    ...mapMutations('commands', ['commitCommandLaunchId']),
    ...mapMutations('recurringEvents', ['closeOccurrenceWizard']),
    setRunningChangeover() {
      const isRunning = this.isMachineHaveChangeoverRunning;
      this.commitRunningChangeovers(
        isRunning ? { changeover_event: this.machineSelected.last_event.event_id } : null,
      );
      this.commitIsModalRunningChangeoverOpen(isRunning);
    },
    /**
     * Get current dashboard from store
     */
    getCurrentDashboard() {
      if (this.$route.name !== 'dashboardId') {
        return null;
      }
      return this.$store.getters['dashboard/getDashboard'](this.$route.params.value);
    },
    /**
     * Closes monitoring switcher
     */
    closeMonitoringSwitcher() {
      this.commitCommandLaunchId(0);
    },
    /**
     * Set the state of the running changeover closure modal
     * @param state
     */
    stateRunningChangeoverClosure(state) {
      if (!state) {
        if (this.runningChangeover !== null) {
          this.addSnackbar({
            id: 'Changeover_closure',
            color: 'color_setups_and_adjustments',
            content: this.$t('changeover.changeoverRunningCanBeClosedFromMenu', { machineName: this.machineSelected?.machine_name }),
            timeout: -1,
            callback: 'openChangeoverClosure',
          });
        } else {
          this.deleteSnackbar('Changeover_closure');
        }
      }

      this.commitIsModalRunningChangeoverOpen(state);
    },
    /**
     * Closes the running changeover modal after a successful manual closure.
     * Unlike `stateRunningChangeoverClosure(false)`, this does not re-queue the
     * "can be closed from the menu" snackbar, since the changeover is already closed.
     */
    closeChangeoverClosureModal() {
      this.deleteSnackbar('Changeover_closure');
      this.commitRunningChangeovers(null);
      this.commitIsModalRunningChangeoverOpen(false);
    },
  },
};
</script>
